import { Fragment, useMemo, type ReactNode } from 'react';
import { RELATION_PART_OF } from '@backstage/catalog-model';
import { Button, ButtonIcon } from '@backstage/ui';
import {
  entityRouteRef,
  getEntityRelations,
  useAsyncEntity,
  useEntityPresentation,
  useEntityRefLink,
} from '@backstage/plugin-catalog-react';
import { useRouteRefParams } from '@backstage/frontend-plugin-api';
import type { EntityHeaderLayoutProps } from '@backstage/plugin-catalog-react/alpha';
import {
  CloudResourceEntity,
  getRuleSeverityFromResources,
  InsightEntity,
  pluralize,
  RuleEntity,
  RuleSetEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import { RegionChip } from '../RegionChip';
import { getConsoleUrl } from '../CloudConsoleLink';
import {
  convertDateFormat,
  useRulesByRuleSet,
} from '@internal/backstage-plugin-devops-react';
import { EntityChip, type EntityChipTone } from './EntityChip';
import { BreadcrumbTrail } from './AccountPageHeader';
import { HeaderTabs } from './HeaderTabs';
import styles from './KindPageHeader.module.css';

const KIND_LABELS: Record<string, string> = {
  CloudResource: 'Cloud Resource',
  RuleSet: 'Rule Set',
  Rule: 'Rule',
  Insight: 'Insight',
};

const RESULT_TONE: Record<string, EntityChipTone> = {
  Failed: 'danger',
  Passed: 'success',
  Unknown: 'neutral',
};

const SEVERITY_TONE: Record<string, EntityChipTone> = {
  Critical: 'danger',
  High: 'warning',
  Medium: 'info',
  Low: 'neutral',
  Info: 'neutral',
};

const FINDING_STATUS_TONE: Record<string, EntityChipTone> = {
  Active: 'danger',
  Resolved: 'success',
  Archived: 'neutral',
};

const catDotClass = (category: string): string => {
  if (category === 'Security') {
    return styles.catDotSecurity;
  }
  if (category === 'Finance') {
    return styles.catDotFinance;
  }
  return styles.catDotHousekeeping;
};

/** Copy glyph matching the stroke-icon family of the shell. */
const CopyIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);

const humanizeType = (type: string): string =>
  (type || '')
    .split('_')
    .filter(Boolean)
    .map(part => part[0].toUpperCase() + part.slice(1))
    .join(' ');

/**
 * Client-side rule counts for the RuleSet subtitle (total / failed /
 * unknown), mirroring the ruleset mockup.
 */
const RuleSetCounts = ({ name }: { name: string }) => {
  const { rules } = useRulesByRuleSet(name ?? '');
  const failed = rules.filter(
    rule => (rule as { spec?: { result?: string } }).spec?.result === 'Failed',
  ).length;
  const passed = rules.filter(
    rule => (rule as { spec?: { result?: string } }).spec?.result === 'Passed',
  ).length;
  const unknown = rules.length - failed - passed;
  return (
    <>
      {pluralize(rules.length, 'rule')}
      {failed > 0 && <b> · {failed} failed</b>}
      {unknown > 0 && ` · ${unknown} unknown`}
    </>
  );
};

/**
 * Entity header for the catalog child kinds (Rule, CloudResource, RuleSet,
 * Insight), 1:1 with the kind-page mockups: title row with the kind badge,
 * status/severity chips and kind actions, a facts subtitle and the tab row.
 * Scanning/settings stay on the account header by design.
 */
const KindPageHeader = ({ tabs, activeTabId }: EntityHeaderLayoutProps) => {
  const { entity } = useAsyncEntity();
  const routeParams = useRouteRefParams(entityRouteRef);
  const entityLink = useEntityRefLink();
  const presentation = useEntityPresentation(entity ?? routeParams);

  const accountRelations = useMemo(
    () =>
      entity
        ? getEntityRelations(entity, RELATION_PART_OF, { kind: 'account' })
        : [],
    [entity],
  );

  const breadcrumbs = useMemo(() => {
    const trail = accountRelations.map(account => ({
      label: account.name,
      href: entityLink(account),
    }));
    if (entity) {
      trail.push({
        label: presentation.primaryTitle,
        href: entityLink(entity),
      });
    }
    return trail;
  }, [accountRelations, entity, entityLink, presentation.primaryTitle]);

  const chips: ReactNode[] = [];
  const subtitleSegments: ReactNode[] = [];
  let actions: ReactNode;
  let titleMono = false;

  const accountLink =
    accountRelations.length > 0 ? (
      <Fragment key="account">
        part of{' '}
        <a href={entityLink(accountRelations[0])}>{accountRelations[0].name}</a>
      </Fragment>
    ) : null;

  if (entity?.kind === 'Rule') {
    const rule = entity as RuleEntity;
    const worst = getRuleSeverityFromResources(
      rule.spec?.resources as Array<{ severity?: string }>,
    );
    chips.push(<EntityChip tone="info">{KIND_LABELS.Rule}</EntityChip>);
    if (rule.spec?.result) {
      chips.push(
        <EntityChip tone={RESULT_TONE[rule.spec.result] ?? 'neutral'}>
          {rule.spec.result}
        </EntityChip>,
      );
    }
    if (worst) {
      chips.push(
        <EntityChip
          tone={SEVERITY_TONE[worst] ?? 'neutral'}
          title="Highest of spec.resources[].severity — per-resource values in the Resources tab"
        >
          {worst}
        </EntityChip>,
      );
    }
    if (rule.spec?.type) {
      subtitleSegments.push(
        <span key="code" className={styles.code} title="Rule code">
          {rule.spec.type}
        </span>,
      );
    }
    if (rule.spec?.category) {
      subtitleSegments.push(
        <Fragment key="category">
          Category <b>{rule.spec.category}</b>
        </Fragment>,
      );
    }
    if (rule.spec?.resourceType) {
      subtitleSegments.push(
        <Fragment key="resourceType">
          Resource type <b>{rule.spec.resourceType}</b>
        </Fragment>,
      );
    }
    if (accountLink) {
      subtitleSegments.push(accountLink);
    }
    const lastScan = convertDateFormat(rule.spec?.reportCreationTime);
    if (lastScan) {
      subtitleSegments.push(`last scan ${lastScan}`);
    }
  } else if (entity?.kind === 'CloudResource') {
    const resource = entity as CloudResourceEntity;
    titleMono = true;
    chips.push(
      <EntityChip tone="info">{KIND_LABELS.CloudResource}</EntityChip>,
    );
    if (resource.spec?.provider) {
      chips.push(
        <EntityChip tone="neutral">
          {resource.spec.provider.toUpperCase()}
        </EntityChip>,
      );
    }
    if (resource.spec?.region) {
      chips.push(<RegionChip region={resource.spec.region} />);
    }
    if (resource.metadata.title && resource.metadata.title !== presentation.primaryTitle) {
      subtitleSegments.push(
        <Fragment key="name">
          <b>{resource.metadata.title}</b>
        </Fragment>,
      );
    }
    if (resource.spec?.service) {
      subtitleSegments.push(
        <Fragment key="service">
          Service <b>{resource.spec.service}</b>
        </Fragment>,
      );
    }
    if (resource.spec?.category) {
      subtitleSegments.push(
        <Fragment key="category">
          Category <b>{resource.spec.category}</b>
        </Fragment>,
      );
    }
    if (accountLink) {
      subtitleSegments.push(accountLink);
    }
    const lastUpdated = convertDateFormat(resource.spec?.resourcesUpdateTime);
    if (lastUpdated) {
      subtitleSegments.push(`last updated ${lastUpdated}`);
    }
    // Same fallback chain as the resources table (legacy entities carry the
    // link in cloudFields until the next scan refreshes spec.consoleUrl).
    const consoleUrl = getConsoleUrl(resource);
    if (consoleUrl) {
      actions = (
        <>
          <Button
            variant="primary"
            onPress={() =>
              window.open(consoleUrl, '_blank', 'noopener,noreferrer')
            }
          >
            Open in {resource.spec.provider?.toUpperCase() ?? 'cloud'} console ↗
          </Button>
          <ButtonIcon
            icon={<CopyIcon />}
            aria-label="Copy resource ID"
            onPress={() => {
              try {
                void navigator.clipboard.writeText(
                  resource.metadata.name ?? '',
                );
              } catch {
                // Clipboard access denied — the id stays selectable.
              }
            }}
          />
        </>
      );
    }
  } else if (entity?.kind === 'RuleSet') {
    const ruleSet = entity as RuleSetEntity;
    chips.push(<EntityChip tone="info">{KIND_LABELS.RuleSet}</EntityChip>);
    const rulesetType = (
      ruleSet.spec?.rulesetType ??
      ruleSet.spec?.type ??
      ''
    )
      .toLowerCase();
    if (ruleSet.spec?.rulesetType || ruleSet.spec?.type) {
      chips.push(
        <EntityChip tone={rulesetType === 'controls' ? 'neutral' : 'info'}>
          {rulesetType === 'controls' ? 'Controls' : 'Benchmark'}
        </EntityChip>,
      );
    }
    // spec.type is the category (the rulesetType pill above holds the type).
    if (ruleSet.spec?.rulesetType && ruleSet.spec?.type) {
      chips.push(
        <EntityChip tone="neutral">
          <span className={`${styles.catDot} ${catDotClass(ruleSet.spec.type)}`} />
          {ruleSet.spec.type}
        </EntityChip>,
      );
    }
    subtitleSegments.push(<RuleSetCounts key="counts" name={ruleSet.metadata.name} />);
    if (accountLink) {
      subtitleSegments.push(accountLink);
    }
  } else if (entity?.kind === 'Insight') {
    const insight = entity as InsightEntity;
    chips.push(<EntityChip tone="info">{KIND_LABELS.Insight}</EntityChip>);
    if (insight.spec?.status) {
      chips.push(
        <EntityChip tone={FINDING_STATUS_TONE[insight.spec.status] ?? 'neutral'}>
          {insight.spec.status}
        </EntityChip>,
      );
    }
    if (insight.spec?.type) {
      chips.push(
        <EntityChip tone="neutral">{humanizeType(insight.spec.type)}</EntityChip>,
      );
    }
    if ((insight.spec?.region ?? '').toLowerCase() === 'global') {
      chips.push(<EntityChip tone="neutral">Global</EntityChip>);
    } else if (insight.spec?.region) {
      chips.push(<RegionChip region={insight.spec.region} />);
    }
    if (insight.spec?.resource) {
      subtitleSegments.push(
        <Fragment key="resource">
          Resource <span className={styles.code}>{insight.spec.resource}</span>
        </Fragment>,
      );
    }
    if (accountLink) {
      subtitleSegments.push(accountLink);
    }
    const updated = convertDateFormat(insight.spec?.updatedAt);
    if (updated) {
      subtitleSegments.push(`updated ${updated}`);
    }
    actions = (
      <>
        <span title="Requires the ArchiveFinding action in cloud-tools">
          <Button variant="secondary" isDisabled>
            Archive
          </Button>
        </span>
        <span title="Requires the resolve flow in cloud-tools">
          <Button variant="secondary" isDisabled>
            Resolve
          </Button>
        </span>
      </>
    );
  }

  return (
    <BreadcrumbTrail entries={breadcrumbs}>
      <div className={styles.root}>
        <div className={styles.titleRow}>
          <h1
            className={`${styles.title} ${titleMono ? styles.titleMono : ''}`}
          >
            {presentation.primaryTitle}
          </h1>
          {entity && chips}
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
        {subtitleSegments.length > 0 && (
          <p className={styles.subtitle}>
            {subtitleSegments.map((segment, index) => (
              <Fragment key={index}>
                {index > 0 && ' · '}
                {segment}
              </Fragment>
            ))}
          </p>
        )}
        <HeaderTabs tabs={tabs} activeTabId={activeTabId} />
      </div>
    </BreadcrumbTrail>
  );
};

export { KindPageHeader };
